import type { ChatMessage, ChatMessageFeedback } from "../types/frontend";
import { materiomaiApiClient, ApiError } from "../api/client";
import { TriggerOption } from "../types/api";

/**
 * Sends a message to the chat API with optional sessionId
 * @param message - The message content
 * @param history - The conversation history
 * @param sessionId - Optional session identifier
 * @param signal - Optional AbortSignal to cancel the request
 * @returns {Promise<Response>} The fetch response object
 * @throws {ApiError} If the request fails
 */
export const sendMessage = async (
  message: ChatMessage,
  history: ChatMessage[],
  sessionId?: string,
  signal?: AbortSignal
): Promise<Response> => {
  try {
    return await materiomaiApiClient.postStream(
      "/api/v1/chat",
      {
        message,
        history,
        sessionId,
      },
      signal
    );
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError("Failed to send chat message", undefined, "CHAT_ERROR");
  }
};

/**
 * Sends a message to the chat API with optional sessionId
 * @param message - The message content
 * @param history - The conversation history
 * @param sessionId - Optional session identifier
 * @param searchSources - Optional flag to search for sources
 * @param signal - Optional AbortSignal to cancel the request
 * @returns {Promise<Response>} The fetch response object
 * @throws {ApiError} If the request fails
 */
export const sendMessageWithGroundedMateriomFormulationsResponse = async (
  message: ChatMessage,
  history: ChatMessage[],
  sessionId?: string,
  searchSources?: boolean,
  signal?: AbortSignal
): Promise<Response> => {
  const trigger = searchSources ? TriggerOption.ON : TriggerOption.OFF;
  try {
    return await materiomaiApiClient.postStream(
      `/api/v1/chat/grounded/formulations?trigger=${trigger}`,
      {
        message,
        history,
        sessionId,
      },
      signal
    );
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError("Failed to send chat message", undefined, "CHAT_ERROR");
  }
};

/**
 * Sends a message to the chat API with optional sessionId
 * @param message - The message content
 * @param history - The conversation history
 * @param sessionId - Optional session identifier
 * @param searchSources - Optional flag to search for sources
 * @param signal - Optional AbortSignal to cancel the request
 * @returns {Promise<Response>} The fetch response object
 * @throws {ApiError} If the request fails
 */
export const sendMessageWithGroundedAcademicLiteratureResponse = async (
  message: ChatMessage,
  history: ChatMessage[],
  sessionId?: string,
  searchSources?: boolean,
  signal?: AbortSignal
): Promise<Response> => {
  const trigger = searchSources ? TriggerOption.ON : TriggerOption.OFF;
  try {
    return await materiomaiApiClient.postStream(
      `/api/v1/chat/grounded/academic_literature?trigger=${trigger}`,
      {
        message,
        history,
        sessionId,
      },
      signal
    );
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError("Failed to send chat message", undefined, "CHAT_ERROR");
  }
};

/**
 * Submits user feedback for a chat message to the backend.
 * @param traceId - The unique identifier for the chat trace/message.
 * @param payload- The feedback payload containing score, reasons, and optional comment.
 * @returns {Promise<Response>} The fetch response object
 * @throws {ApiError} If the request fails
 */
export const submitFeedback = async (
  traceId: string,
  payload: ChatMessageFeedback
): Promise<Response> => {
  // Attempt to send feedback to the backend API
  try {
    return await materiomaiApiClient.post(
      `/api/v1/chat/${traceId}/feedback`,
      payload
    );
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(
      "Failed to submit feedback",
      undefined,
      "FEEDBACK_ERROR"
    );
  }
};

/**
 * Request payload for agent chat endpoint
 */
export interface AgentRequest {
  message: string;
  n_researchers?: number;
  deep_reasoning_enabled?: boolean;
  session_id?: string;
}

/**
 * Sends a message to the agent endpoint.
 * @param request - The request payload containing message and optional configuration
 * @param signal - Optional AbortSignal to cancel the request
 * @returns {Promise<Response>} The fetch response object with streaming body
 * @throws {ApiError} If the request fails
 */
export const sendMessageToAgent = async (
  request: AgentRequest,
  signal?: AbortSignal
): Promise<Response> => {
  try {
    return await materiomaiApiClient.postStream(
      "/api/v1/chat/agent",
      request,
      signal
    );
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(
      "Failed to send message to Agent",
      undefined,
      "AGENT_ERROR"
    );
  }
};
